import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import classnames from 'classnames';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Types
import { ReviewState } from 'Shared/types/Review.type';
import { FormTypeType } from 'Shared/types/ReviewFormTemplate.type';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Utils
import { isInThePast } from 'Shared/utils/DateTime.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';

// Actions
import * as ReviewActions from 'Shared/actions/Reviews.actions';

// Selectors
import * as UserSelectors from 'Shared/selectors/Users.selectors';
import * as ReviewSelectors from 'Shared/selectors/Reviews.selectors';

// Components
import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpBadge, WpBadgeList } from 'Wpds/components/badge';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import ReviewProgressBar from 'Dashboard/components/ThreeSixtyReviewPartials/ReviewProgressBar/ReviewProgressBar';
import StatusDescription from 'Dashboard/components/ThreeSixtyReviewPartials/StatusDescription/StatusDescription';
import ReviewDueDate from 'Dashboard/components/ThreeSixtyReviewPartials/ReviewDueDate/ReviewDueDate';
import EditAndUnshareOptionsMenu from 'Dashboard/components/ThreeSixtyReviewPartials/EditAndUnshareOptionsMenu/EditAndUnshareOptionsMenu';

// Styles
import './ReviewCard.scss';

type Props = {
  review;
  classname?: string;
  isPeerReviewForm?: boolean;
};

const ReviewCard = ({ review, classname, isPeerReviewForm = false }: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isUnlocking, toggleIsUnlocking] = useToggle(false);

  const currentUserSlug = useSelector(state => state.user.slug);

  const slug = ReviewsHelpers.getSlug(review);
  const self360 = ReviewsHelpers.getSelf360(review);

  const compilerSlug = ReviewsHelpers.getCompilerSlug(review);
  const compiler = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: compilerSlug }));
  const compilerName = UserHelpers.getFullName(compiler);

  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);
  const receiver = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: receiverSlug }));
  const receiverName = UserHelpers.getFullName(receiver);
  const receiverAvatar = UserHelpers.getAvatar(receiver).toJS();

  const state = ReviewsHelpers.getState(review);
  const dueBy = ReviewsHelpers.getDuBy(review);
  const peerReviewDueBy = ReviewsHelpers.getPeerReviewDueBy(review);
  const showDueDate = state === ReviewState.PEER_REVIEW || state === ReviewState.COMPILATION;

  const peerReviewForm = useSelector(state =>
    ReviewSelectors.getReviewFormsByReviewSlugAndUserSlugAndFormType(state, {
      reviewSlug: slug,
      userSlug: currentUserSlug,
      formType: FormTypeType.self_peer_review,
    })
  );

  const selfReviewForm = useSelector(state =>
    ReviewSelectors.getReviewFormsByReviewSlugAndUserSlugAndFormType(state, {
      reviewSlug: slug,
      userSlug: currentUserSlug,
      formType: FormTypeType.peer_review,
    })
  );

  const reviewForm = selfReviewForm || peerReviewForm;

  const compilationForm = useSelector(state =>
    ReviewSelectors.getReviewFormsByReviewSlugAndType(state, {
      reviewSlug: slug,
      formType: FormTypeType.compilation,
    })
  )?.first();

  const isCurrentUserCompiler = ReviewsUtils.isCurrentUserCompiler({ review, currentUserSlug });
  const isCurrentUserCreator = ReviewsUtils.isCurrentUserCreator({ review, currentUserSlug });
  const isCurrentUserReceiver = ReviewsUtils.isCurrentUserReceiver({ review, currentUserSlug });

  const showProgressBar =
    !isPeerReviewForm &&
    (isCurrentUserCompiler || isCurrentUserCreator) &&
    state !== ReviewState.SHARED &&
    state !== ReviewState.COMPLETED;

  const handleOnClick = () => {
    if (isPeerReviewForm) {
      const formSlug = ReviewFormHelpers.getSlug(reviewForm);
      history.push(PageUrlsUtils.makePeerReviewFormUrl(formSlug));
    } else if (
      state === ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED ||
      state === ReviewState.SHARED ||
      state === ReviewState.COMPLETED
    ) {
      history.push(PageUrlsUtils.makeReviewShareUrl(slug));
    } else history.push(PageUrlsUtils.makeReviewCompilationUrl(slug));
  };

  const handleUnlock = () => {
    toggleIsUnlocking();
    const data = {
      review: {
        unlock: true,
      },
    };
    dispatch(ReviewActions.updateReview({ reviewSlug: slug, data })).then(() => {
      WpToastManager.success('Review shared');
      toggleIsUnlocking();
    });
  };

  const baseClassName = 'ReviewCard';
  const classes = classnames(baseClassName, classname);

  const Badges = () => {
    const isPastDue =
      (!self360 &&
        isInThePast(dueBy) &&
        state !== ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED &&
        state !== ReviewState.SHARED) ||
      (isPeerReviewForm && isInThePast(peerReviewDueBy));

    const startedAt = ReviewFormHelpers.getStartedAt(reviewForm);
    const submittedAt = ReviewFormHelpers.getSubmittedAt(reviewForm);
    const receiverViewedAt = ReviewsHelpers.getReceiverViewedAt(review);
    const isPeerReviewDraft =
      isPeerReviewForm && Boolean(startedAt) && !submittedAt && state === ReviewState.PEER_REVIEW;

    const compilationStartedAt = ReviewFormHelpers.getStartedAt(compilationForm);
    const compilationSubmittedAt = ReviewFormHelpers.getSubmittedAt(compilationForm);
    const isCompilationDraft =
      !isPeerReviewForm &&
      Boolean(compilationStartedAt) &&
      !compilationSubmittedAt &&
      (state === ReviewState.PEER_REVIEW || state === ReviewState.COMPILATION);

    const showSeenBadge = !self360 && state === ReviewState.SHARED && isCurrentUserCompiler && receiverViewedAt;

    return (
      <WpBadgeList>
        {showSeenBadge && <WpBadge>Seen by {receiverName}</WpBadge>}
        {isPastDue && <WpBadge color="orange">Overdue</WpBadge>}
        {isPeerReviewDraft && <WpBadge>Draft</WpBadge>}
        {isCompilationDraft && <WpBadge>Draft</WpBadge>}
      </WpBadgeList>
    );
  };

  const CTAButton = () => {
    if (isPeerReviewForm) {
      return (
        <WpButton variant="secondary" size="sm">
          {isCurrentUserReceiver ? 'Start Self Feedback' : 'Give Peer Feedback'}
        </WpButton>
      );
    }
    if (isCurrentUserCompiler && state === ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED) {
      return (
        <div onClick={e => e.stopPropagation()}>
          <WpButtonGroup spacing="md">
            <WpButton variant="secondary" size="sm" onClick={handleUnlock} loading={isUnlocking}>
              Share with {receiverName}
            </WpButton>
            <EditAndUnshareOptionsMenu review={review} />
          </WpButtonGroup>
        </div>
      );
    } else if (isCurrentUserCompiler && state === ReviewState.COMPILATION) {
      return (
        <WpButton variant="secondary" size="sm">
          Write 360 Feedback
        </WpButton>
      );
    } else if (isCurrentUserCompiler && (state === ReviewState.SHARED || state === ReviewState.COMPLETED)) {
      return (
        <div onClick={e => e.stopPropagation()}>
          <WpButtonGroup spacing="md">
            <WpButton variant="secondary" size="sm" onClick={handleOnClick}>
              View
            </WpButton>
            {!self360 && <EditAndUnshareOptionsMenu review={review} />}
          </WpButtonGroup>
        </div>
      );
    } else {
      return (
        <WpButton variant="tertiary" size="sm" onClick={handleOnClick}>
          View
        </WpButton>
      );
    }
  };

  const Title = () => {
    if (isCurrentUserCompiler && state === ReviewState.SHARED && !self360) {
      return `You gave ${receiverName} a 360 Feedback`;
    } else if (isCurrentUserCompiler && (state === ReviewState.SHARED || state === ReviewState.COMPLETED) && self360) {
      return `You received a self 360 Feedback`;
    } else if (isCurrentUserReceiver && state === ReviewState.SHARED) {
      return `${compilerName} shared your 360 Feedback`;
    }
    return receiverName;
  };

  return (
    <WpContainer className={classes} onClick={handleOnClick}>
      <WpContainerBody padding="05">
        <div className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__body__avatar`}>
            <WpAvatar avatar={receiverAvatar} size="xl" />
          </div>
          <div className={`${baseClassName}__body__details`}>
            <div className={`${baseClassName}__body__details__header`}>
              <p className={`${baseClassName}__body__details__header__name`}>
                <Title />
              </p>
              <div className={`${baseClassName}__body__details__header__badges`}>
                <Badges />
              </div>
              <div className={`${baseClassName}__body__details__header__button`}>
                <CTAButton />
              </div>
            </div>
            <div className={`${baseClassName}__body__details__description`}>
              <StatusDescription review={review} isPeerReviewForm={isPeerReviewForm} />
              {showDueDate && (
                <>
                  {' '}
                  (<ReviewDueDate review={review} isPeerReviewForm={isPeerReviewForm} />)
                </>
              )}
            </div>
            {showProgressBar && <ReviewProgressBar review={review} />}
          </div>
        </div>
      </WpContainerBody>
    </WpContainer>
  );
};

export default ReviewCard;
